'use client';
import React, { Suspense } from 'react';
import Image from 'next/image';
import { PortableText } from '@portabletext/react';
import Header from '@/components/global/partials/Header';
import Footer from '@/components/global/partials/Footer';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { PageSuccessStoriesOverviewSchema } from '@/sanity/pages/PageSuccessStoriesOverviewTemplate';
import ArticleGrid from '@/components/pageComponents/successStoriesOverview/ArticleGrid';
import ArticleGridSkeleton from '@/components/pageComponents/successStoriesOverview/ArticleGridSkeleton';
import heroMobileBackground from '@/public/global/hero-mobile-background.png';
import heroDesktopBackground from '@/public/global/hero-desktop-background-md.png';
import ScrollingBanner from '../pageComponents/global/ScrollingBanner';
import { motion } from 'framer-motion';
import HeaderV2 from '../global/partials/HeaderV2';
import CertificateSectionV2 from '../pageComponents/global/sections/CertificateSectionV2';
import TestimonialsSectionV2 from '../pageComponents/global/sections/TestimonialsSectionV2';
import WhatSetsApartSection from '../pageComponents/global/sections/WhatSetsApartSection';
import { DEFAULT_LANGUAGE } from '@/sanity/globals/I18n';

const ease = [0.25, 0.1, 0.25, 1] as const;

export default function PageSuccessStoriesOverviewJSX({
  data,
}: {
  data: PageSuccessStoriesOverviewSchema;
}) {
  return (
    <main className="flex flex-col items-center bg-white">
      {data?.settings?.header && (
        <HeaderV2
          data={data.settings.header}
          headerStyle="light"
          headerType={'main'} // showBorder
          language={data.settings?.language}
        />
      )}

      {/* ── Hero ── */}
      <section className="relative w-full flex flex-col items-center pt-[7rem] lg:pt-[22rem] lg:pb-[8rem] pb-[3.62rem] text-center overflow-hidden lg:min-h-[60rem]">
        <motion.div
          initial={{ opacity: 0, filter: 'blur(8px)' }}
          animate={{ opacity: 1, filter: 'blur(0px)' }}
          transition={{ duration: 1.2, ease }}
          className="absolute w-full h-full top-[0rem] lg:hidden block z-0 pointer-events-none"
        >
          <Image
            src={heroMobileBackground}
            alt=""
            className="w-full h-full object-cover object-center"
          />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, filter: 'blur(8px)' }}
          animate={{ opacity: 1, filter: 'blur(0px)' }}
          transition={{ duration: 1.2, ease }}
          className="absolute w-full h-full top-[0rem] hidden lg:block z-0 pointer-events-none"
        >
          <Image
            src={heroDesktopBackground}
            alt=""
            className="w-full h-full object-cover object-center"
          />
        </motion.div>

        <div className="relative z-10 flex flex-col items-center gap-[1.25rem] lg:gap-[2.5rem] mx-auto w-[min(94.25rem,calc(100%-4rem))] lg:w-[min(94.25rem,calc(100%-8rem))]">
          {data.heroChip && (
            <p className="font-blacker italic text-[2.25rem] text-black opacity-70">
              {data.heroChip}
            </p>
          )}
          {data.heroTitle && (
            <div className="richtext flex flex-col gap-[1.8rem] prose-h1:text-[4.5rem] prose-h1:lg:text-[7rem]">
              <PortableText
                value={data.heroTitle}
                components={richTextComponent}
              />
            </div>
          )}
        </div>
      </section>

      {/* ── Article grid ── */}
      <Suspense fallback={<ArticleGridSkeleton />}>
        <ArticleGrid articles={data.articles ?? []} language={data?.settings?.language ?? DEFAULT_LANGUAGE} />
      </Suspense>

      <CertificateSectionV2
        data={{
          chip: data.certificateChip,
          title: data.certificateTitle,
          text: data.certificateText,
          highlightedText: data.certificateHighlightedText,
          images: data.certificateImages,
        }}
        language={data?.settings?.language ?? DEFAULT_LANGUAGE}
      />
      <TestimonialsSectionV2
        className="lg:pt-[10rem]"
        language={data.settings?.language}
        data={data}
      />
      <WhatSetsApartSection data={data} />

      <ScrollingBanner
        className="bg-[#f4f5f5] border-y-0"
        words={data.scrollingWords?.words}
        wordsI18n={data.scrollingWords?.wordsI18n}
      />

      {data?.settings?.footer && <Footer data={data.settings.footer} finalCta={data} language={data?.settings?.language} />}
    </main>
  );
}
